"use client"

import Image from "next/image"
import { Anchor } from "../../app/components/anchor"
import { DriverProfile } from "@/lib/drivers/types"
import { serviceIcons } from "@/constants/drivers/service-icons"
import { getDriverExperience } from "@/lib/drivers/date-utils"

const formatVerificationName = (key: string): string => {
  switch (key) {
    case "backgroundCheck":
      return "Background Checked"
    case "identity":
      return "Identity Verified"
    case "usda":
      return "USDA Certified"
    case "manual":
      return "Manual Verified"
    case "insurance":
      return "Insurance Verified"
    case "usdot":
      return "USDOT Certified"
    default:
      return key
  }
}

interface DriverCardProps {
  profile: DriverProfile
}

export function DriverCard({ profile }: DriverCardProps) {
  const { data } = profile

  const photoUrl = data.profilePhoto?.small || null

  return (
    <Anchor
      href={`/drivers/${data.id}`}
      className="group no-underline hover:no-underline block bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200"
    >
      <div className="p-6 space-y-4">
        <div className="flex items-center space-x-4">
          {photoUrl ? (
            <Image
              src={photoUrl}
              alt={data.displayName || "Driver photo"}
              width={64}
              height={64}
              className="rounded-full object-cover"
              unoptimized
            />
          ) : (
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
              <span className="text-gray-500">No image</span>
            </div>
          )}
          <div>
            <h2 className="text-xl font-semibold group-hover:underline">
              {data.displayName}
            </h2>
            <p className="text-gray-600 text-sm">
              Member since {getDriverExperience(data.joinedAt).joinYear}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-gray-700 line-clamp-2">{data.about}</p>

          <div className="flex items-center space-x-2">
            <span className="text-yellow-500">★</span>
            <span className="font-medium">{data.rating.toFixed(1)}</span>
            <span className="text-gray-500">({data.ratingsCount} reviews)</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {Object.entries(data.verifications).map(
              ([key, value]) =>
                value && (
                  <span
                    key={key}
                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                  >
                    {formatVerificationName(key)}
                  </span>
                ),
            )}
          </div>

          {profile.data.offerings?.length > 0 &&
            profile.data.offerings[0]?.services?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {profile.data.offerings[0].services.map(
                  (service: { name: string; id: string }) => (
                    <div
                      key={service.id}
                      className="flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                    >
                      <span className="mr-1">
                        {
                          serviceIcons[
                            service.name as keyof typeof serviceIcons
                          ]
                        }
                      </span>
                      {service.name}
                    </div>
                  ),
                )}
              </div>
            )}

          <div className="text-sm text-gray-600">
            {data.wonShipments} shipments completed
          </div>
        </div>
      </div>
    </Anchor>
  )
}
